import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import type { CampaignStatus, AdvertiserStatus } from '../../lib/api/types';

/**
 * Design-system primitives.
 *
 * <p>Every screen is assembled from these rather than styled ad hoc, so the panel stays coherent as
 * it grows and a token change lands everywhere at once. No component below contains a hex value:
 * colours come from the semantic names in tailwind.config.js.
 */

function cx(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'destructive' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  // Electric Blue is reserved for the primary action. One per view, or it stops meaning anything.
  primary: 'bg-electric-blue text-text-primary hover:brightness-110 disabled:opacity-50',
  secondary:
    'bg-surface-tertiary text-text-primary border border-surface-tertiary hover:brightness-125 disabled:opacity-50',
  destructive: 'bg-status-error text-text-primary hover:brightness-110 disabled:opacity-50',
  ghost: 'text-text-secondary hover:text-text-primary hover:bg-surface-secondary',
};

export function Button({
  variant = 'secondary',
  loading = false,
  disabled,
  children,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded px-4 py-2 text-body-md font-medium transition',
        'disabled:cursor-not-allowed',
        BUTTON_VARIANTS[variant],
        className,
      )}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

// ---------------------------------------------------------------------------
// Form controls
// ---------------------------------------------------------------------------

interface FieldProps {
  label: string;
  htmlFor?: string;
  /** Server-side validation surfaces here, on the offending field rather than only as a banner. */
  error?: string;
  hint?: string;
  children: ReactNode;
}

export function Field({ label, htmlFor, error, hint, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-label-md font-mono uppercase text-text-secondary">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-body-md text-text-secondary">{hint}</p>}
      {error && (
        <p role="alert" className="text-body-md text-status-error">
          {error}
        </p>
      )}
    </div>
  );
}

const CONTROL_CLASSES =
  'w-full rounded border border-surface-tertiary bg-surface-primary px-3 py-2 text-body-md ' +
  'text-text-primary placeholder:text-text-secondary focus:border-electric-blue';

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(CONTROL_CLASSES, className)} />;
}

/** Numbers are monospaced so digits align when several inputs sit in a column. */
export function NumberInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} type="number" className={cx(CONTROL_CLASSES, 'font-mono', className)} />;
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={cx(CONTROL_CLASSES, className)}>
      {children}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

/** Depth through tonal layering and a 1px border, never a shadow. */
export function Card({
  title,
  action,
  children,
  className,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx(
        'rounded-lg border border-surface-tertiary bg-surface-secondary p-6',
        className,
      )}
    >
      {(title || action) && (
        <header className="mb-4 flex items-center justify-between gap-4">
          {title && <h2 className="text-headline-sm text-text-primary">{title}</h2>}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * A headline figure.
 *
 * <p>The value is monospaced so a row of cards reads as a set of comparable numbers rather than as
 * prose.
 */
export function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-surface-tertiary bg-surface-secondary p-6">
      <p className="text-label-md font-mono uppercase text-text-secondary">{label}</p>
      <p className="mt-2 font-mono text-headline-lg text-text-primary">{value}</p>
      {hint && <p className="mt-1 text-body-md text-text-secondary">{hint}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

const CAMPAIGN_STATUS_STYLES: Record<CampaignStatus, string> = {
  ACTIVE: 'bg-status-active/15 text-status-active border-status-active/40',
  PAUSED: 'bg-status-paused/15 text-status-paused border-status-paused/40',
  BUDGET_EXHAUSTED: 'bg-status-error/15 text-status-error border-status-error/40',
  FINISHED: 'bg-surface-tertiary text-text-secondary border-surface-tertiary',
  DRAFT: 'bg-surface-tertiary text-text-secondary border-surface-tertiary',
};

const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  ACTIVE: 'Activa',
  PAUSED: 'Pausada',
  BUDGET_EXHAUSTED: 'Presupuesto agotado',
  FINISHED: 'Finalizada',
  DRAFT: 'Borrador',
};

/**
 * A campaign's status.
 *
 * <p>Always carries text. Status is never communicated by colour alone — that would be unreadable
 * to a colour-blind operator and invisible in a printed report.
 */
export function StatusBadge({ status }: { status: CampaignStatus }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded border px-2 py-0.5 text-label-md font-mono uppercase',
        CAMPAIGN_STATUS_STYLES[status],
      )}
    >
      {CAMPAIGN_STATUS_LABELS[status]}
    </span>
  );
}

export function AdvertiserStatusBadge({ status }: { status: AdvertiserStatus }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded border px-2 py-0.5 text-label-md font-mono uppercase',
        status === 'ACTIVE'
          ? 'border-status-active/40 bg-status-active/15 text-status-active'
          : 'border-surface-tertiary bg-surface-tertiary text-text-secondary',
      )}
    >
      {status === 'ACTIVE' ? 'Activo' : 'Inactivo'}
    </span>
  );
}

/** Budget consumption, so a campaign about to exhaust is visible at a glance. */
export function BudgetBar({ consumed, total }: { consumed: number; total: number }) {
  const percentage = total > 0 ? Math.min(100, (consumed / total) * 100) : 0;
  // Amber past 80%: the point of the bar is to give warning before delivery stops, not after.
  const tone =
    percentage >= 100 ? 'bg-status-error' : percentage >= 80 ? 'bg-status-paused' : 'bg-electric-blue';

  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-surface-tertiary"
      role="progressbar"
      aria-valuenow={Math.round(percentage)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className={cx('h-full transition-all', tone)} style={{ width: `${percentage}%` }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

/** Skeletons match the shape of the final layout, so loading does not shift the page. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded bg-surface-tertiary', className)} />;
}

export function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="flex gap-4">
          {Array.from({ length: columns }).map((_, column) => (
            <Skeleton key={column} className="h-5 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** An empty state explains what to do next; a blank panel just looks broken. */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-surface-tertiary px-6 py-12 text-center">
      <h3 className="text-headline-sm text-text-primary">{title}</h3>
      <p className="max-w-md text-body-md text-text-secondary">{description}</p>
      {action}
    </div>
  );
}

/** An error state always offers a retry, because most failures here are transient. */
export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-status-error/40 bg-status-error/10 px-6 py-10 text-center">
      <h3 className="text-headline-sm text-text-primary">Algo salió mal</h3>
      <p className="max-w-md text-body-md text-text-secondary">{message}</p>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          Reintentar
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export function Table({ children }: { children: ReactNode }) {
  // Wide tables scroll inside their own container rather than breaking the page layout.
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-body-md">{children}</table>
    </div>
  );
}

export function Th({ children, numeric }: { children: ReactNode; numeric?: boolean }) {
  return (
    <th
      className={cx(
        'sticky top-0 z-10 border-b border-surface-tertiary bg-surface-secondary px-4 py-3',
        'text-label-md font-mono uppercase text-text-secondary',
        numeric ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  numeric,
  className,
}: {
  children: ReactNode;
  numeric?: boolean;
  className?: string;
}) {
  return (
    <td
      className={cx(
        // Compact padding in management tables, to maximise visible rows.
        'border-b border-surface-tertiary px-4 py-2 text-text-primary',
        numeric && 'text-right font-mono',
        className,
      )}
    >
      {children}
    </td>
  );
}
